import * as Api from '@ViewModels';
import { useQueryClient } from '@tanstack/react-query';
import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { useDebounceValue } from '../../../../hooks/useDebounceValue';
import { IAutomationInfo } from '../../../../models';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useLocalNotificationService } from '../../../../models/LocalNotifications';
import { Topics } from '../../../../models/LocalNotificationTopics';
import { useEventLogging } from '../../../../models/Logging';
import {
	useAutomationContactPreviewMutation,
	useAutomationTemplatesByStatusQuery,
	useCancelAutomationMutation,
	useStartAutomationContactMutation,
	useStartAutomationForContactMutation,
	useStartAutomationMutation,
} from '../../../../queries';
import type { IPreviewCreateAutomationResult } from '../../../../extViewmodels/sdk/models';
import { Dropdown, DropdownContent, DropdownItem, DropdownSelectTrigger } from '../../../components-v2/Dropdown';
import { brandPrimary, grayIconFill } from '../../../styles/colors';
import { baseStyleSheet } from '../../../styles/styles';
import { Checkbox } from '../../Checkbox';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../ConfirmationDialog';
import { LoadingSpinner } from '../../LoadingSpinner';
import { Modal } from '../../Modal';
import { AutomationsIcon } from '../../svgs/icons/AutomationsIcon';
import { SearchIcon } from '../../svgs/icons/SearchIcon';
import { TextInput } from '../../TextInput';
import { AutomationStepStatusEmailEditorModal } from '../AutomationStepStatusEmailEditor';
import { BulkAutomationsCreationConfirmation } from '../BulkAutomationsCreationConfirmation';
import { AutomationStepIcons } from './AutomationStepIcons';
import { AutomationStepsBy } from './AutomationStepsBy';
import { AutomationStepTitleAndDescription } from './AutomationStepTitleAndDescription';
import { styleSheet } from './styles';

enum AutomationSelectionStep {
	PreSelectedStep = 'PreSelectedStep',
	SelectedStep = 'SelectedStep',
}

export type IAutomationSelectorContactSelection = {
	includeContactIds?: string[];
	excludeContactIds?: string[];
};

export const AutomationSelectorModal = ({
	contact,
	contactSelection,
	className,
	createAutomationForContact,
	filterCriteria,
	ownershipFilter,
	onDeselectAll,
	startAutomationForSelectedContacts,
	styles,
	closeModal,
	onCancel,
	onSave,
}: {
	className?: string;
	contact?: Api.IContact;
	contactSelection?: IAutomationSelectorContactSelection;
	createAutomationForContact?: boolean;
	filterCriteria?: Api.IContactFilterCriteria;
	ownershipFilter?: Api.IOwnershipFilter;
	onDeselectAll?: () => void;
	startAutomationForSelectedContacts?: boolean;
	styles?: StyleDeclarationValue[];
	closeModal: () => void;
	onCancel?: () => void;
	onSave?: (automation?: Api.IAutomation<Api.IAutomationStep>) => void;
}) => {
	const queryClient = useQueryClient();
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const { postNotification } = useLocalNotificationService();
	const { logEvent, logApiError } = useEventLogging('AutomationSelector');

	const [automationSelectionComesFrom, setAutomationSelectionComesFrom] = React.useState<Api.AutomationSelectionType>(
		Api.AutomationSelectionType.Myself
	);
	const [automationSelectionStep, setAutomationSelectionStep] = React.useState<AutomationSelectionStep>(
		AutomationSelectionStep.PreSelectedStep
	);
	const [createdAutomation, setCreatedAutomation] = React.useState<Api.IAutomation | null>(null);
	const [emailStepStatusToEdit, setEmailStepStatusToEdit] =
		React.useState<Api.IAutomationStepStatus<Api.IEmailAutomationStep> | null>(null);
	const [isOnHold, setIsOnHold] = React.useState(false);
	const [preview, setPreview] = React.useState<IPreviewCreateAutomationResult | null>(null);
	const [selectedEmployee, setSelectedEmployee] = React.useState<Api.IUser | null>(null);
	const [selectedTemplate, setSelectedTemplate] = React.useState<Api.IAutomationTemplateReference | null>(null);
	const [showBulkCreateConfirmation, setShowBulkCreateConfirmation] = React.useState(false);
	const [sendToHousehold, setSendToHousehold] = React.useState(
		selectedTemplate?.sendToHousehold || userSession.account.features.households?.sendToHouseholdByDefault
	);
	const [showUnsubscribedWarning, setShowUnsubscribedWarning] = React.useState(false);
	const [searchInput, setSearchInput] = React.useState('');
	const [selectedCreatorId, setSelectedCreatorId] = React.useState<string | null>(null);
	const searchText = useDebounceValue(searchInput, 300);

	const automationTemplatesQuery = useAutomationTemplatesByStatusQuery({
		status: Api.TemplateStatus.Published,
		meta: {
			toastErrorMessage: 'Error loading automations',
			errorLogging: {
				category: 'AutomationSelectorModal',
				action: 'AutomationsTemplates-Error',
			},
		},
	});

	const onSendToHouseholdChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSendToHousehold(e.target.checked);
	};
	const automationTemplates = React.useMemo<Api.IAutomationTemplateReference[]>(() => {
		return automationTemplatesQuery.data?.filter(template => !!template) || [];
	}, [automationTemplatesQuery.data]);

	const selectedVisibleSteps = (selectedTemplate && selectedTemplate?.publishedSteps?.slice(0, 5)) || [];

	const onSelectionChanged = (type: Api.AutomationSelectionType) => {
		setAutomationSelectionComesFrom(type);
	};

	const getDescription = (template: Api.IAutomationTemplateReference, fullDesc = false) => {
		const publishedSteps = template.publishedSteps ?? [];
		return `${publishedSteps.length} Step${publishedSteps.length > 1 ? 's' : ''} ${
			fullDesc ? `- ${template.runtimeDays}-day runtime` : ''
		}`;
	};

	const resetAllState = () => {
		setAutomationSelectionComesFrom(Api.AutomationSelectionType.Myself);
		setAutomationSelectionStep(AutomationSelectionStep.PreSelectedStep);
		setIsOnHold(false);
		setPreview(null);
		setSelectedEmployee(null);
		setSelectedTemplate(null);
		setShowBulkCreateConfirmation(false);
		setShowUnsubscribedWarning(false);
		setSearchInput('');
		setSelectedCreatorId(null);
	};

	const creatorOptions = React.useMemo(() => {
		if (!automationTemplates.length) {
			return [{ title: 'Show all creators', value: null }];
		}
		try {
			const creatorSet = new Set<string>();
			const options: { title: string; value: string }[] = [];
			for (const template of automationTemplates) {
				const creator = template.creator;
				if (creator?.id && !creatorSet.has(creator.id)) {
					creatorSet.add(creator.id);

					const name =
						creator.firstName && creator.lastName
							? `${creator.firstName} ${creator.lastName}`.trim()
							: creator.email || 'Unknown Creator';
					options.push({
						title: name,
						value: creator.id,
					});
				}
			}
			// Sort and prepend "Show all creators"
			options.sort((a, b) => a.title.localeCompare(b.title));
			return [{ title: 'Show all creators', value: null }, ...options];
		} catch {
			return [{ title: 'Show all creators', value: null }];
		}
	}, [automationTemplates]);

	const filteredAutomationTemplates = React.useMemo(() => {
		try {
			return automationTemplates.filter((template: Api.IAutomationTemplateReference) => {
				// Search text filter - case insensitive search on automation name
				const searchMatch =
					!searchText || (template.name && template.name.toLowerCase().includes(searchText.toLowerCase()));

				// Creator filter - match by creator ID
				const creatorMatch = !selectedCreatorId || template.creator?.id === selectedCreatorId;

				return searchMatch && creatorMatch;
			});
		} catch {
			return automationTemplates;
		}
	}, [automationTemplates, searchText, selectedCreatorId]);

	const onUserSelected = (selection: Api.IUser) => {
		setSelectedEmployee(selection);
	};

	const handleCancelAutomationSelection = () => {
		resetAllState();
	};

	const handleSearchTextChange = React.useCallback((searchInputValue: string) => {
		setSearchInput(searchInputValue);
	}, []);

	const handleCreatorFilterChange = React.useCallback((creatorId: string | null) => {
		setSelectedCreatorId(creatorId);
	}, []);

	const handleNextClick = () => {
		try {
			if (selectedTemplate?.allowUnsubscribed === true) {
				setShowUnsubscribedWarning(true);
			} else {
				setShowBulkCreateConfirmation(true);
			}
		} catch (error) {
			logEvent('HandleNextClick-Error', { error: error instanceof Error ? error.message : String(error) });
			setShowBulkCreateConfirmation(true);
		}
	};

	const onUnsubscribedWarningConfirm = (confirmed: boolean) => {
		setShowUnsubscribedWarning(false);
		if (confirmed) {
			setShowBulkCreateConfirmation(true);
		}
	};

	const unsubscribedWarningOptions: IConfirmationDialogOption<boolean>[] = [
		{
			isCta: true,
			representedObject: true,
			title: 'Continue',
		},
		{
			isCancel: true,
			representedObject: false,
			title: 'Cancel',
		},
	];

	const onEditEmailStepStatusModalRequestClose = async (_: any, canceled?: boolean) => {
		if (canceled) {
			await cancelAutomation();
			onCancel?.();
		} else {
			await startAutomation();
			onFinishCreatingContactAutomation(createdAutomation);
		}

		setCreatedAutomation(null);
		setEmailStepStatusToEdit(null);
	};

	const onBulkCreateConfirmationRequestClose = (result?: IConfirmationDialogOption<boolean>, canceled?: boolean) => {
		if (!!result?.isCta && !!result?.representedObject && !canceled) {
			createForContacts(selectedTemplate);
		}

		resetAllState();
	};

	const onChangedOnHold = () => {
		setIsOnHold(!isOnHold);
	};

	const close = () => {
		closeModal();
		onCancel?.();
	};

	const getUniqueValuesBasedOnIds = (contacts: Partial<Api.IProjectedContact>[]) => {
		const uniqueIds = new Set<string>();
		const vals = (contacts || []).filter(x => {
			if (!uniqueIds.has(x.id)) {
				uniqueIds.add(x.id);
				return true;
			}
			return false;
		});
		return vals;
	};

	const handleStartAutomationNotifications = ({
		createAutomationRequest,
		template,
	}: {
		createAutomationRequest: Api.ICreateAutomationRequest;
		template: Api.IAutomationTemplateReference;
	}) => {
		logEvent('BulkStartAutomation', { createAutomationRequest, templateId: template.id });
		toast.success('Automation started for selected contacts.');

		const info: IAutomationInfo = {
			createAutomationFilterRequest: createAutomationRequest,
		};
		postNotification?.({
			info,
			topic: Topics.CREATE_AUTOMATION,
		});
	};

	const startAutomationContactMutation = useStartAutomationContactMutation({
		onError: (error: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
			logApiError('StartAutomationContact-Error', error);
		},
	});

	const startAutomationMutation = useStartAutomationMutation({
		onError: (error: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
		},
		onSuccess: () => {
			logEvent('StartAutomation', { id: createdAutomation.id });
			closeModal();
		},
	});

	const cancelAutomationContactMutation = useCancelAutomationMutation({
		onError: (error: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
		},
		onSuccess: () => {
			logEvent('CancelAutomation', { id: createdAutomation.id });
			postNotification?.({
				info: createdAutomation,
				topic: Topics.DELETE_AUTOMATION,
			});
		},
	});

	const automationContactPreviewMutation = useAutomationContactPreviewMutation({
		onError: (error: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
		},
		onSuccess: (result: Api.IPreviewCreateAutomationResult) => {
			const isAdmin = userSession.account.isAdmin && userSession.account.features.automation.allowAdminToStartOnBehalf;

			setAutomationSelectionStep(
				isAdmin ? AutomationSelectionStep.SelectedStep : AutomationSelectionStep.PreSelectedStep
			);
			setShowBulkCreateConfirmation(!isAdmin);

			result.excludeContacts = getUniqueValuesBasedOnIds(result.excludeContacts || []);
			result.contactOwners = getUniqueValuesBasedOnIds(result.contactOwners || []);
			setPreview(result);
		},
	});

	const startAutomationForContactMutation = useStartAutomationForContactMutation({
		onError: (error: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
		},
		onSuccess: async ({
			template,
			automation,
		}: {
			template: Api.IAutomationTemplateReference;
			automation: Api.IAutomation;
		}) => {
			const hasEmailAsFirstStep =
				template.publishedSteps?.length > 0 && template.publishedSteps[0]?._type === 'EmailAutomationStep';

			// Invalidate the contact cache so the UI refetches with updated inProgressAutomations.
			// The server already updated inProgressAutomations when starting the automation.
			queryClient.invalidateQueries({ queryKey: ['contact'] });

			logEvent('CreateAutomation-Success', {
				automationId: automation.id,
				contactId: contact.id,
				templateId: template.id,
			});
			if (hasEmailAsFirstStep) {
				const firstStepStatus = automation.steps?.length > 0 ? automation.steps[0] : null;
				if (firstStepStatus?.step._type === 'EmailAutomationStep') {
					setCreatedAutomation(automation);
					setEmailStepStatusToEdit(firstStepStatus as Api.IAutomationStepStatus<Api.IEmailAutomationStep>);
					return;
				} else {
					await startAutomation();
					onFinishCreatingContactAutomation(automation);
					return;
				}
			}
			onFinishCreatingContactAutomation(automation);
			closeModal();
		},
	});

	const onTemplateSelected = async (template: Api.IAutomationTemplateReference) => {
		if (!!createAutomationForContact && !!contact?.id) {
			const hasEmailAsFirstStep =
				template.publishedSteps?.length > 0 && template.publishedSteps[0]?._type === 'EmailAutomationStep';

			await startAutomationForContactMutation.mutateAsync({
				template,
				contactId: contact.id,
				start: !hasEmailAsFirstStep,
			});

			return;
		} else if (!!startAutomationForSelectedContacts && !!contactSelection) {
			const request = getCreateAutomationRequestForContacts();
			await automationContactPreviewMutation.mutateAsync({
				id: template.id,
				createAutomationRequest: request,
			});

			setSelectedTemplate(template);
			setSendToHousehold(template.sendToHousehold || userSession.account.features.households?.sendToHouseholdByDefault);
		}
		onSave?.();
	};

	const cancelAutomation = async () => {
		const automationId = createdAutomation.id;
		return cancelAutomationContactMutation.mutateAsync({ automationId });
	};

	const startAutomation = async () => {
		const automationId = createdAutomation.id;
		return startAutomationMutation.mutateAsync({ automationId });
	};

	const onFinishCreatingContactAutomation = (automation: Api.IAutomation<Api.IAutomationStep>) => {
		const info: IAutomationInfo = {
			automation,
			contact,
		};

		postNotification?.({
			info,
			topic: Topics.CREATE_AUTOMATION,
		});
		const contactName = contact?.handle || contact?.firstName || contact?.lastName || 'contact';
		toast.success(`Automation started for ${contactName}.`);
		onSave?.(automation);
	};

	const createForContacts = async (template: Api.IAutomationTemplateReference) => {
		if (!!template && !!startAutomationForSelectedContacts && !!contactSelection) {
			const request = getCreateAutomationRequestForContacts();

			// This query will fail throw a bugsnag error if the error is not swallowed.
			//   The reason is that valid errors to be displayed are intended to come back in the error messages.
			try {
				await startAutomationContactMutation.mutateAsync({
					id: template.id,
					createAutomationRequest: request,
				});
				handleStartAutomationNotifications({ createAutomationRequest: request, template });
				onDeselectAll?.();

				onSave?.();
				closeModal();
			} catch {
				// Swallow the error to allow the error modal to display why automation failed
			}
		}
	};

	const getCreateAutomationRequestForContacts = () => {
		const isAdmin = userSession.account.isAdmin && userSession.account.features.automation.allowAdminToStartOnBehalf;

		if (contactSelection) {
			const request: Api.ICreateAutomationRequest = {
				autoStart: true,
				excludeContactIds: contactSelection.excludeContactIds,
				filter: !contactSelection.includeContactIds?.length ? filterCriteria : undefined,
				ownershipFilter: !contactSelection.includeContactIds?.length ? ownershipFilter : undefined,
				includeContactIds: contactSelection.includeContactIds,
				putOnHold: isOnHold,
				groupByHousehold: sendToHousehold,
			};

			if (isAdmin) {
				request.sendFromOptions = {
					mode:
						automationSelectionComesFrom === Api.AutomationSelectionType.ContactOwners
							? Api.SendEmailFrom.ContactOwner
							: Api.SendEmailFrom.CurrentUser,
				};
				if (automationSelectionComesFrom === Api.AutomationSelectionType.Employee) {
					request.sendFromOptions.mode = Api.SendEmailFrom.SelectedUser;
					request.sendFromOptions.selectedUser = `${selectedEmployee.id}`;
				}
			}
			return request;
		}
	};

	const isLoading = automationTemplatesQuery.isPending;

	return (
		<Modal isOpen useDefaultHeader={true} onRequestClose={close}>
			<div className={`${css(styleSheet.container, ...(styles || []))} ${className || ''}`}>
				<h2 className={css(styleSheet.header)}>
					<AutomationsIcon fillColor={brandPrimary} />
					{automationSelectionStep === AutomationSelectionStep.PreSelectedStep && ' Select an Automation Flow'}
					{automationSelectionStep === AutomationSelectionStep.SelectedStep && ' Selected Automation Flow'}
				</h2>

				<article
					className={css(
						styleSheet.body,
						automationSelectionStep === AutomationSelectionStep.SelectedStep && styleSheet.bodyOverflowVisible
					)}
				>
					{isLoading ? (
						<LoadingSpinner type='large' />
					) : (
						<>
							{automationSelectionStep === AutomationSelectionStep.PreSelectedStep && (
								<>
									<div className={css(styleSheet.filtersContainer)}>
										<div className={css(styleSheet.modalFilters)}>
											<div className={css(styleSheet.modalSearchContainer)} role='search'>
												<TextInput
													type='text'
													inputId='search-automations-modal'
													placeholder='Search automation name'
													value={searchInput}
													onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearchTextChange(e.target.value)}
													aria-label='Search automation templates by name'
													leftAccessory={
														<SearchIcon className={css(styleSheet.modalSearchIcon)} fillColor={grayIconFill} />
													}
													rightAccessory={
														searchInput ? (
															<button
																type='button'
																className={css(styleSheet.modalClearButton)}
																onClick={() => handleSearchTextChange('')}
																aria-label='Clear search input'
															>
																Clear
															</button>
														) : null
													}
													className={css(styleSheet.modalSearchInputWrapper)}
													inputClassName={css(styleSheet.modalSearchInput)}
												/>
											</div>
											<div className={css(styleSheet.modalCreatorFilterContainer)}>
												<Dropdown>
													<DropdownSelectTrigger
														styles={[styleSheet.modalDropdownTrigger]}
														aria-label='Filter automation templates by creator'
													>
														{creatorOptions.find(option => option.value === selectedCreatorId)?.title ||
															'Show all creators'}
													</DropdownSelectTrigger>
													<DropdownContent styles={[styleSheet.modalDropdownContent]}>
														{creatorOptions.map(option => (
															<DropdownItem
																key={option.value || 'all'}
																onClick={() => handleCreatorFilterChange(option.value)}
																styles={[styleSheet.modalDropdownItem]}
															>
																{option.title}
															</DropdownItem>
														))}
													</DropdownContent>
												</Dropdown>
											</div>
										</div>
									</div>
									{filteredAutomationTemplates.length > 0 ? (
										<ul className={css(baseStyleSheet.verticalStack)}>
											{filteredAutomationTemplates.map(automationTemplate => {
												const publishedStepReferences = automationTemplate.publishedSteps;
												const visibleSteps = publishedStepReferences?.slice(0, 5);
												const overflowSteps =
													publishedStepReferences?.length > 5 ? publishedStepReferences.slice(5) : [];

												return (
													<li className={css(styleSheet.automationCard)} key={automationTemplate.id}>
														<AutomationStepTitleAndDescription
															title={automationTemplate.name}
															description={getDescription(automationTemplate, true)}
														/>
														<div className={css(styleSheet.automationCardSteps)}>
															<div className={css(baseStyleSheet.horizontalStack)}>
																<AutomationStepIcons visibleSteps={visibleSteps} />
																{overflowSteps?.length > 0 && (
																	<span
																		className={css(baseStyleSheet.tableColumnHeader, styleSheet.automationCardLabel)}
																	>
																		{`... ${overflowSteps.length} more`}
																	</span>
																)}
															</div>
															<button
																className={css(
																	baseStyleSheet.ctaButtonReverseSmall,
																	styleSheet.automationCardSelectButton
																)}
																disabled={isLoading}
																onClick={() => onTemplateSelected(automationTemplate)}
															>
																<span>Select</span>
															</button>
														</div>
														{isLoading && (
															<LoadingSpinner className={css(baseStyleSheet.absoluteCenter)} type='large' />
														)}
													</li>
												);
											})}
										</ul>
									) : (
										<div className={css(styleSheet.emptyState)}>No results found</div>
									)}
								</>
							)}
						</>
					)}

					{automationSelectionStep === AutomationSelectionStep.SelectedStep && selectedTemplate && (
						<div>
							<article className={css(styleSheet.selectedAutomation)}>
								<AutomationStepTitleAndDescription
									title={selectedTemplate.name}
									description={`${getDescription(selectedTemplate)}`}
								/>
								<div className={css(styleSheet.automationCardSteps)}>
									<div className={css(baseStyleSheet.horizontalStack)}>
										<AutomationStepIcons visibleSteps={selectedVisibleSteps} />
									</div>
								</div>
							</article>
							<article>
								<h4 className={css(baseStyleSheet.truncateText, styleSheet.automationCardSubTitle)}>
									Automation steps by:
								</h4>
								<AutomationStepsBy
									preview={preview}
									isBusy={isLoading}
									userSession={userSession}
									onAutomationSelectionComesFrom={onSelectionChanged}
									onSelectedEmployee={onUserSelected}
								/>
								{userSession.account.features.households?.enabled ? (
									<>
										<Checkbox
											className={css(styleSheet.sendToHoseholdCheckbox)}
											checked={sendToHousehold}
											type='large'
											onChange={onSendToHouseholdChanged}
										>
											<div>
												<p className={css(styleSheet.checkboxText)}>Send to households?</p>
											</div>
										</Checkbox>
										<p className={css(styleSheet.sendToHouseholdMessage)}>
											When this setting is turned on, if a contact in the automation is part of a household, the other
											household member will be included on any emails and handwritten cards sent as part of this
											automation.
										</p>
									</>
								) : null}
								<footer>
									<div className={css(styleSheet.buttonContainer)}>
										<button
											className={css(baseStyleSheet.ctaButton, styleSheet.ctaButton)}
											disabled={
												automationSelectionComesFrom === Api.AutomationSelectionType.Employee && !selectedEmployee
											}
											onClick={handleNextClick}
										>
											Next
										</button>
										<button className={css(baseStyleSheet.ctaButtonReverse)} onClick={handleCancelAutomationSelection}>
											Cancel
										</button>
									</div>
								</footer>
							</article>
						</div>
					)}
				</article>

				<BulkAutomationsCreationConfirmation
					allowStartAutomationsOnHold={userSession.account?.features?.automation?.allowStartAutomationsOnHold}
					isOnHold={isOnHold}
					isOpen={showBulkCreateConfirmation}
					onChangedOnHold={onChangedOnHold}
					onRequestClose={onBulkCreateConfirmationRequestClose}
					preview={preview}
				/>

				<AutomationStepStatusEmailEditorModal
					contact={contact as any}
					emailStepStatus={emailStepStatusToEdit}
					modalProps={{
						isOpen: !!emailStepStatusToEdit,
						onRequestClose: onEditEmailStepStatusModalRequestClose,
					}}
					saveCtaTitle='Send'
				/>

				<ConfirmationDialog
					className={css(styleSheet.narrowConfirmationDialog)}
					modalProps={{
						isOpen: showUnsubscribedWarning,
						onRequestClose: (result?: IConfirmationDialogOption<boolean>, cancel?: boolean) => {
							onUnsubscribedWarningConfirm(!!result?.representedObject && !cancel);
						},
					}}
					options={unsubscribedWarningOptions}
					title='Send to Unsubscribed Contacts'
				>
					<div style={{ textAlign: 'center' }}>
						This automation will send communications to unsubscribed contacts. This should only be used for critical
						transactional communications. Do you want to continue?
					</div>
				</ConfirmationDialog>
			</div>
		</Modal>
	);
};
